/**
 * Product-analytics event catalog (IC-132, ADR-6/ADR-10).
 *
 * The single source of truth for what may leave the browser. Property values
 * are enums, counts and bucket strings only: "what the user did, never what
 * they saw". `report_key` is a catalog identifier, never free text.
 *
 * Adding an event or a property is additive: extend `AnalyticsEvents` and
 * `EVENT_PROP_KEYS` together (the compiler enforces it), add a
 * `CATALOG_FIXTURES` entry in `__tests__/guard-helpers.ts` (the compiler
 * enforces that too) and add a sanitizer test.
 *
 * Scalar props only, no free-text `detail` strings: every string value is a
 * member of an exported union below.
 */

export type RowBucket = '0' | '1-100' | '101-1k' | '1k-10k' | '10k+';

export function rowCountBucket(n: number): RowBucket {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n <= 100) return '1-100';
  if (n <= 1000) return '101-1k';
  if (n <= 10000) return '1k-10k';
  return '10k+';
}

export type ReportViewKind = 'default' | 'published' | 'user' | 'draft';

/** The eight dashboard panel item types, declared locally so `lib/analytics` never imports from components. */
export type PanelItemTypeToken = 'grid' | 'chart' | 'textBox' | 'pivot' | 'boundImage' | 'comboBox' | 'card' | 'listBox';

/** Ambient scope props, filled from `AnalyticsScope` context by the emit hooks. */
export interface AnalyticsScopeProps {
  host?: 'report' | 'tool';
  report_key?: string;
  tool?: string;
  layout_type?: 'grid' | 'composite' | 'dashboard';
  surface?: 'grid' | 'dashboard_panel' | 'composite_member';
  item_type?: PanelItemTypeToken;
}

export const SCOPE_PROP_KEYS = ['host', 'report_key', 'tool', 'layout_type', 'surface', 'item_type'] as const;

export type GridColumnAction =
  | 'sort_asc' | 'sort_desc' | 'sort_then_asc' | 'sort_then_desc' | 'sort_clear'
  | 'group' | 'ungroup' | 'group_reorder' | 'expand_all_groups' | 'collapse_all_groups' | 'toggle_group_bar'
  | 'pin' | 'unpin' | 'hide' | 'show' | 'rename' | 'reorder' | 'align' | 'format' | 'aggregate'
  | 'open_cond_format' | 'best_fit' | 'best_fit_all' | 'toggle_auto_width' | 'open_filter_editor'
  | 'open_find' | 'open_column_chooser' | 'toggle_footer' | 'toggle_filter_row';

export type GridEntry =
  | 'header_click' | 'column_menu' | 'footer_menu' | 'group_panel' | 'drag' | 'column_chooser' | 'view_menu' | 'keyboard';

export const FORMAT_TOKENS = [
  'number_2', 'general_short', 'general_long', 'percent_2', 'percent_0', 'percent_value_2', 'percent_value_0',
  'currency_2', 'currency_0',
  'date_short', 'date_padded', 'date_medium', 'date_long', 'date_iso', 'month_year',
  'datetime_24h', 'datetime_long_short', 'datetime_long_long', 'datetime_iso_min', 'datetime_iso_sec',
  'time_short', 'time_long', 'time_24h',
] as const;
export type FormatToken = (typeof FORMAT_TOKENS)[number];

export type FilterOperator =
  | 'equal' | 'notEqual' | 'contains' | 'notContains' | 'isLike' | 'isNotLike' | 'beginsWith' | 'endsWith'
  | 'greaterThan' | 'greaterThanOrEqual' | 'lessThan' | 'lessThanOrEqual' | 'between' | 'notBetween' | 'in';

export type ColumnTypeToken = 'string' | 'number' | 'currency' | 'date' | 'boolean';
export type ViewTypeToken = 'grid' | 'dashboard';
export type ViewActionKind =
  | 'switch' | 'new_grid' | 'new_dashboard' | 'rename' | 'delete' | 'unpublish' | 'reset_layout' | 'result_sets_changed';
export type ChartTypeToken = 'cartesian' | 'pie' | 'donut';
export type PanelEntry = 'actions_menu' | 'panel_header' | 'empty_state' | 'invalid_config';

export interface GridColumnActionProps extends AnalyticsScopeProps {
  action: GridColumnAction;
  entry: GridEntry;
  column_field?: string;
  column_computed?: boolean;
  column_type?: ColumnTypeToken;
  direction?: 'asc' | 'desc';
  sort_count?: number;
  group_depth?: number;
  pin_side?: 'left' | 'right' | 'none';
  align?: 'left' | 'center' | 'right' | 'none';
  format_code?: FormatToken | 'default';
  aggregate?: 'sum' | 'min' | 'max' | 'count' | 'avg' | 'none';
  enabled?: boolean;
  outcome?: 'committed' | 'cancelled' | 'cleared';
  has_existing_rules?: boolean;
  has_compound_filter?: boolean;
  from_index?: number;
  to_index?: number;
  visible_count?: number;
  total_count?: number;
}

export interface GridFilterAppliedProps extends AnalyticsScopeProps {
  operator?: FilterOperator;
  column_type?: ColumnTypeToken;
  condition_count: number;
  group_count: number;
  distinct_operator_count: number;
  has_range_operator: boolean;
  conjunction?: 'and' | 'or' | 'mixed';
}

export interface GridFilterClearedProps extends AnalyticsScopeProps {
  entry: 'column_menu' | 'filter_row' | 'popover' | 'filter_editor';
  target: 'column' | 'all';
  column_field?: string;
  column_computed?: boolean;
}

export interface GridCondFormatAppliedProps extends AnalyticsScopeProps {
  rule_count: number;
  row_rule_count: number;
  expression_rule_count: number;
  cleared: boolean;
  column_field?: string;
  column_computed?: boolean;
}

export interface GridRowMenuActionProps extends AnalyticsScopeProps {
  /** The tool-manifest `command.id`, a code identifier; never the menu text. */
  action: string;
}

export interface ReportResultTabSwitchedProps extends AnalyticsScopeProps {
  result_set_index: number;
  result_set_count: number;
}

export interface CompositeTabActionProps extends AnalyticsScopeProps {
  action: 'select' | 'close';
  member_index: number;
  member_count: number;
  first_visit?: boolean;
  member_report_key?: string;
}

export interface ViewActionProps extends AnalyticsScopeProps {
  action: ViewActionKind;
  view_type?: ViewTypeToken;
  from_kind?: ReportViewKind;
  to_kind?: ReportViewKind;
  view_kind?: ReportViewKind;
  was_active?: boolean;
  cleared_draft?: boolean;
  visible_count?: number;
  total_count?: number;
  entry?: 'view_menu' | 'actions_menu';
}

export interface ViewSavedProps extends AnalyticsScopeProps {
  mode: 'save' | 'publish';
  quick: boolean;
  is_new: boolean;
  view_type: ViewTypeToken;
  sort_count?: number;
  sort_non_default?: boolean;
  has_group?: boolean;
  has_filter?: boolean;
  has_find?: boolean;
  has_cond_format?: boolean;
  has_hidden_cols?: boolean;
  has_frozen?: boolean;
  has_aggregates?: boolean;
  has_formats?: boolean;
  has_renamed?: boolean;
  has_align?: boolean;
  has_reordered?: boolean;
  hide_footer?: boolean;
  hide_group_bar?: boolean;
  has_multi_result_sets?: boolean;
  panel_count?: number;
  grid_panels?: number;
  chart_panels?: number;
  pivot_panels?: number;
  card_panels?: number;
  text_panels?: number;
  other_panels?: number;
  crossfilter_edges?: number;
  locked_panels?: number;
  has_layout_settings?: boolean;
}

export interface DashboardPanelActionProps extends AnalyticsScopeProps {
  action: 'add' | 'configure' | 'remove' | 'lock' | 'unlock';
  chart_type?: ChartTypeToken;
  entry?: PanelEntry;
  result_set_picker_shown?: boolean;
  changed_count?: number;
  changed_fields?: boolean;
  changed_aggregation?: boolean;
  changed_format?: boolean;
  changed_grouping?: boolean;
  changed_selection_mode?: boolean;
  changed_style?: boolean;
  changed_title?: boolean;
  changed_content?: boolean;
}

export interface DashboardLayoutActionProps extends AnalyticsScopeProps {
  action: 'auto_arrange' | 'settings_changed' | 'show_grid_toggled';
  packing?: 'vertical' | 'free';
  spacing?: 'compact' | 'comfortable' | 'spacious';
  packing_changed?: boolean;
  spacing_changed?: boolean;
  locked_count?: number;
  enabled?: boolean;
}

export interface DashboardCrossfilterEditedProps extends AnalyticsScopeProps {
  action: 'add' | 'remove';
  source_type: PanelItemTypeToken;
  target_type: PanelItemTypeToken;
  target_mode: 'single' | 'multiple';
}

export interface DashboardPanelInteractedProps extends AnalyticsScopeProps {
  interaction: 'select' | 'clear' | 'detail_close' | 'detail_collapse' | 'detail_expand';
  is_filter_source: boolean;
  selection_mode?: 'single' | 'multiple';
  chart_type?: ChartTypeToken;
  via?: 'click' | 'keyboard';
}

export interface DashboardEditorOpenedProps extends AnalyticsScopeProps {
  editor: 'add' | 'configure' | 'relationships' | 'layout';
  entry: PanelEntry;
}

export interface AnalyticsEvents {
  app_loaded: Record<string, never>;
  report_opened: {
    report_key: string;
    layout_type: 'grid' | 'composite' | 'dashboard';
    entry: 'menu' | 'search';
  };
  report_run: {
    report_key: string;
    outcome: 'success' | 'error' | 'canceled';
    trigger: 'auto' | 'user';
    duration_ms: number;
    row_count_bucket: RowBucket;
    result_set_count: number;
    param_count: number;
    execution_profile?: 'warm' | 'dedicated' | 'split' | 'sync';
  };
  report_exported: {
    report_key: string;
    format: 'xlsx' | 'pdf';
    view_kind: ReportViewKind;
    row_count_bucket: RowBucket;
    outcome: 'success' | 'error';
  };
  grid_column_action: GridColumnActionProps;
  grid_filter_applied: GridFilterAppliedProps;
  grid_filter_cleared: GridFilterClearedProps;
  grid_cond_format_applied: GridCondFormatAppliedProps;
  grid_row_menu_action: GridRowMenuActionProps;
  report_result_tab_switched: ReportResultTabSwitchedProps;
  composite_tab_action: CompositeTabActionProps;
  view_action: ViewActionProps;
  view_saved: ViewSavedProps;
  dashboard_panel_action: DashboardPanelActionProps;
  dashboard_layout_action: DashboardLayoutActionProps;
  dashboard_crossfilter_edited: DashboardCrossfilterEditedProps;
  dashboard_panel_interacted: DashboardPanelInteractedProps;
  dashboard_editor_opened: DashboardEditorOpenedProps;
}

export type AnalyticsEventName = keyof AnalyticsEvents;

/** Runtime allowed-prop-key map; the mapped type forces it to track the interface. */
export const EVENT_PROP_KEYS: {
  [K in AnalyticsEventName]: readonly (keyof AnalyticsEvents[K] & string)[];
} = {
  app_loaded: [],
  report_opened: ['report_key', 'layout_type', 'entry'],
  report_run: [
    'report_key',
    'outcome',
    'trigger',
    'duration_ms',
    'row_count_bucket',
    'result_set_count',
    'param_count',
    'execution_profile',
  ],
  report_exported: ['report_key', 'format', 'view_kind', 'row_count_bucket', 'outcome'],
  grid_column_action: [...SCOPE_PROP_KEYS, 'action', 'entry', 'column_field', 'column_computed', 'column_type', 'direction', 'sort_count', 'group_depth', 'pin_side', 'align', 'format_code', 'aggregate', 'enabled', 'outcome', 'has_existing_rules', 'has_compound_filter', 'from_index', 'to_index', 'visible_count', 'total_count'],
  grid_filter_applied: [...SCOPE_PROP_KEYS, 'operator', 'column_type', 'condition_count', 'group_count', 'distinct_operator_count', 'has_range_operator', 'conjunction'],
  grid_filter_cleared: [...SCOPE_PROP_KEYS, 'entry', 'target', 'column_field', 'column_computed'],
  grid_cond_format_applied: [...SCOPE_PROP_KEYS, 'rule_count', 'row_rule_count', 'expression_rule_count', 'cleared', 'column_field', 'column_computed'],
  grid_row_menu_action: [...SCOPE_PROP_KEYS, 'action'],
  report_result_tab_switched: [...SCOPE_PROP_KEYS, 'result_set_index', 'result_set_count'],
  composite_tab_action: [...SCOPE_PROP_KEYS, 'action', 'member_index', 'member_count', 'first_visit', 'member_report_key'],
  view_action: [...SCOPE_PROP_KEYS, 'action', 'view_type', 'from_kind', 'to_kind', 'view_kind', 'was_active', 'cleared_draft', 'visible_count', 'total_count', 'entry'],
  view_saved: [...SCOPE_PROP_KEYS, 'mode', 'quick', 'is_new', 'view_type', 'sort_count', 'sort_non_default', 'has_group', 'has_filter', 'has_find', 'has_cond_format', 'has_hidden_cols', 'has_frozen', 'has_aggregates', 'has_formats', 'has_renamed', 'has_align', 'has_reordered', 'hide_footer', 'hide_group_bar', 'has_multi_result_sets', 'panel_count', 'grid_panels', 'chart_panels', 'pivot_panels', 'card_panels', 'text_panels', 'other_panels', 'crossfilter_edges', 'locked_panels', 'has_layout_settings'],
  dashboard_panel_action: [...SCOPE_PROP_KEYS, 'action', 'chart_type', 'entry', 'result_set_picker_shown', 'changed_count', 'changed_fields', 'changed_aggregation', 'changed_format', 'changed_grouping', 'changed_selection_mode', 'changed_style', 'changed_title', 'changed_content'],
  dashboard_layout_action: [...SCOPE_PROP_KEYS, 'action', 'packing', 'spacing', 'packing_changed', 'spacing_changed', 'locked_count', 'enabled'],
  dashboard_crossfilter_edited: [...SCOPE_PROP_KEYS, 'action', 'source_type', 'target_type', 'target_mode'],
  dashboard_panel_interacted: [...SCOPE_PROP_KEYS, 'interaction', 'is_filter_source', 'selection_mode', 'chart_type', 'via'],
  dashboard_editor_opened: [...SCOPE_PROP_KEYS, 'editor', 'entry'],
};

export const ANALYTICS_EVENT_NAMES = Object.keys(EVENT_PROP_KEYS) as AnalyticsEventName[];

export function isCatalogEvent(name: string): name is AnalyticsEventName {
  return (ANALYTICS_EVENT_NAMES as readonly string[]).includes(name);
}
